import { useEffect, createContext, useState, useContext } from "react";
import { ConfigProvider } from "antd";
import { useTranslation } from "react-i18next";
import { antdLocaleFor } from "../i18n/antdLocale";
import * as microsoftTeams from "@microsoft/teams-js";

import {
  FluentProvider,
  teamsLightTheme,
  teamsDarkTheme,
  teamsHighContrastTheme,
  Spinner,
  tokens,
} from "@fluentui/react-components";
import { HashRouter as Router, Navigate, Route, Routes, useLocation, Outlet } from "react-router-dom";
import { useTeamsUserCredential } from "@microsoft/teamsfx-react";
import Privacy from "./Privacy";
import Home from "./Home/Home";
import TermsOfUse from "./TermsOfUse";
import { TeamsFxContext } from "./Context";
import config from "../config";
import Header from "./header/Header";
import VideoDetail from "./videoDetail/VideoDetail";
import { postCreateUserApi, getUserId, sendTimeZone } from "../apis/WebServices";
import useLangSyncOnStartup from "../hooks/useLangSyncOnStartup";
import mobileHttp from "../apis/mobileHttp";
import BookMarked from "./bookMarked/BookMarked";

import VideosCatalogue from "./videoCatalogue/VideosCatalogue";
import BlogLibrary from "./blogLibrary/BlogLibrary";
import { app } from "@microsoft/teams-js";
import BlogDetail from "./blogDetail/BlogDetail";
import Footer from "./footer/Footer";
import Notification from "./notification/Notification";
import AccessDenied from "./AccessDenied";
import TrialBanner from "./TrialBanner";
import TrialExpired from "./TrialExpired";
import ProtectedRoute from "../ProtectedRoute";
import Loader from "./loader/Loader";
import ConsentManager from "./auth/ConsentManager";
import { useEnhancedAuth } from "./auth/useEnhancedAuth";
import { setTeamsCredential, clearTeamsCredential } from "../apis/http";
import { UserContext } from "../contexts/UserContext";
import appInsightsService, { ApplicationInsightsService } from '../services/applicationInsights';
import { withAITracking } from '@microsoft/applicationinsights-react-js';
import mobileEnhancedTracking from '../services/mobileEnhancedTracking';
import mobileAuthHandler from '../apis/mobileAuth';

export { UserContext };

// Global consent state
let globalConsentState = {
  needsConsent: false,
  missingScopes: [],
  setConsentNeeded: null
};

// Hook into fetch errors to detect consent needed
let globalUnhandledRejectionHandler = null;

function App() {
  // Early tracking - capture render attempts on all platforms
  // This does NOT affect authentication or user flow
  if (window.appInsightsEarly) {
    window.appInsightsEarly.trackEvent({
      name: 'AppComponentRendering',
      properties: {
        timestamp: new Date().toISOString(),
        platform: ApplicationInsightsService.detectPlatformFromUA(navigator.userAgent),
        url: window.location.href
      }
    });
  }

  const { i18n: i18nInstance } = useTranslation();
  const [userName, setUserName] = useState("");
  const [userToken, setUserToken] = useState('');
  const [location, setLocation] = useState("");

  // P2-hardening: re-sync language to backend on startup if a previous send failed.
  useLangSyncOnStartup(userToken);
  const [access, setAccess] = useState(true);
  const [accessDenied, setAccessDenied] = useState(true);
  const [isConsentComplete, setIsConsentComplete] = useState(false);
  const [apiConsentNeeded, setApiConsentNeeded] = useState(false);
  const [missingScopes, setMissingScopes] = useState([]);
  const [credentialsReady, setCredentialsReady] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false); // Track sign-in progress
  
  // Trial and licensing state
  const [trialInfo, setTrialInfo] = useState(null);
  const [isExemptTenant, setIsExemptTenant] = useState(false);
  const [hasAccess, setHasAccess] = useState(true); // Default to true until license check completes

  // Initialize Application Insights once on app load
  useEffect(() => {
    // Initialize without history for now (add React Router history if you use it)
    appInsightsService.initialize();
    
    // Make available globally for other components
    window.appInsights = appInsightsService;
  }, []); // Empty dependency array - run once

  // Track when component successfully mounts
  useEffect(() => {
    if (window.appInsightsEarly) {
      window.appInsightsEarly.trackEvent({
        name: 'AppComponentMounted',
        properties: {
          timestamp: new Date().toISOString(),
          platform: ApplicationInsightsService.detectPlatformFromUA(navigator.userAgent)
        }
      });
    }
  }, []); // Run once on mount

  // 1️⃣ CRITICAL FIX: Skip TeamsFx on mobile to avoid popup errors
  const isMobileDevice = /TeamsMobile|Android.*Teams|iPhone.*Teams/i.test(navigator.userAgent);
  
  // Check for mobile auth IMMEDIATELY to prevent sign-in screen flash
  const hasMobileAuth = isMobileDevice && 
    localStorage.getItem('mobile_auth_complete') === 'true' && 
    localStorage.getItem('mobile_auth_user');
  
  // Only use TeamsFx on desktop/web, NOT on mobile
  const teamsFxResult = !isMobileDevice ? useTeamsUserCredential({
    initiateLoginEndpoint: config.initiateLoginEndpoint,
    clientId: config.clientId,
    scopes: ['https://graph.microsoft.com/User.Read']
  }) : {
    loading: false,
    theme: teamsLightTheme,
    themeString: 'default',
    teamsUserCredential: null
  };
  
  const {
    loading: teamsFxLoading,
    theme,
    themeString,
    teamsUserCredential
  } = teamsFxResult;
  
  // Enhanced auth for better error handling
  const enhancedAuth = useEnhancedAuth();
  
  // Force re-render counter for mobile auth
  const [mobileAuthTrigger, setMobileAuthTrigger] = useState(0);
  
  // Use TeamsFx loading state + enhanced auth
  const isAuthenticated = enhancedAuth.isAuthenticated;
  const isLoading = teamsFxLoading || enhancedAuth.isLoading;
  const user = enhancedAuth.user;
  const error = enhancedAuth.error;
  const needsConsent = enhancedAuth.needsConsent;
  
  // 🔧 FIX: Auto-reload when Teams credential becomes available
  useEffect(() => {
    if (teamsUserCredential && !isAuthenticated && !isLoading && !isSigningIn) {
      console.log('🔄 Teams credential available but not authenticated - checking for SSO...');
      
      // Check if Teams completed SSO (after "Almost there!" consent)
      const checkInterval = setInterval(async () => {
        try {
          const token = await teamsUserCredential.getToken(['User.Read']);
          if (token) {
            console.log('✅ SSO completed! Token acquired - reloading...');
            clearInterval(checkInterval);
            window.location.reload();
          }
        } catch (err) {
          console.log('⏳ Still waiting for SSO completion...');
        }
      }, 2000); // Check every 2 seconds
      
      // Stop checking after 10 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        console.log('⏹️ SSO check timeout - user needs to sign in manually');
      }, 10000);
      
      return () => clearInterval(checkInterval);
    }
  }, [teamsUserCredential, isAuthenticated, isLoading, isSigningIn]);
  
  // Debug logging to understand the auth state
  useEffect(() => {
    console.log('🔍 AUTH STATE DEBUG:', {
      isAuthenticated,
      isLoading,
      user: user?.displayName,
      teamsFxLoading,
      enhancedAuthLoading: enhancedAuth.isLoading
    });
  }, [isAuthenticated, isLoading, user, teamsFxLoading, enhancedAuth.isLoading]);

  // Legacy compatibility
  const loading = isLoading;

  // Set up global consent handler with proper cleanup
  useEffect(() => {
    globalConsentState.setConsentNeeded = (needed, scopes) => {
      setApiConsentNeeded(needed);
      setMissingScopes(scopes || []);
    };

    // Set up global unhandled rejection listener
    globalUnhandledRejectionHandler = (event) => {
      if (event.reason && event.reason.needConsent) {
        console.log('🟡 Global consent needed detected:', event.reason.scopes);
        if (globalConsentState.setConsentNeeded) {
          globalConsentState.setConsentNeeded(true, event.reason.scopes);
        }
      }
    };
    
    window.addEventListener('unhandledrejection', globalUnhandledRejectionHandler);
    
    return () => {
      if (globalUnhandledRejectionHandler) {
        window.removeEventListener('unhandledrejection', globalUnhandledRejectionHandler);
        globalUnhandledRejectionHandler = null;
      }
    };
  }, []);

  const getUserIdAndStore = async (email) => {
    const startTime = Date.now();
    
    // Track API call
    appInsightsService.trackEvent('GetUserIdApiCall', {
      email: email || 'none',
      action: 'started'
    });
    
    try {
      const response = await getUserId(email);
      const duration = Date.now() - startTime;
      let timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Track API response
      appInsightsService.trackEvent('GetUserIdApiResponse', {
        email: email || 'none',
        duration,
        isSuccess: response?.isSuccess || false,
        hasData: !!response?.data,
        userId: response?.data?.id || 'none',
        statusCode: response?.statusCode || 'unknown'
      });

      if (response?.isSuccess) {
        setUserToken(response?.data?.id);
        setAccess(true);
        await sendTimeZone(response?.data?.id, timeZone);
        return response.data?.id;
      }

      return null;
    } catch (error) {
      const duration = Date.now() - startTime;
      
      // Track API error
      appInsightsService.trackEvent('GetUserIdApiError', {
        email: email || 'none',
        duration,
        errorMessage: error.message || 'Unknown error',
        errorType: error.name || 'UnknownError',
        errorCode: error.code || 'none',
        statusCode: error.response?.status || 'unknown',
        responseData: JSON.stringify(error.response?.data || {}),
        fullError: JSON.stringify(error, Object.getOwnPropertyNames(error))
      });
      
      return null;
    } finally {
      setAccess(false);
    }
  };

  const fetchUserLocation = async () => {
    try {
      const response = await fetch("https://ipinfo.io/json");
      const userLocation = await response.json();
      setLocation(userLocation?.country);
    } catch (error) {
      // Handle error silently
    }
  };


  const fetchUserInfo = async () => {
    console.log('🚀 fetchUserInfo called');
    
    // Track function call
    appInsightsService.trackEvent('FetchUserInfoCalled', {
      isAuthenticated,
      hasUser: !!user,
      userName: user?.displayName || 'none',
      userEmail: user?.preferredUserName || 'none'
    });
    
    if (!isAuthenticated || !user) {
      console.log('❌ fetchUserInfo: Not authenticated or no user info');
      appInsightsService.trackEvent('FetchUserInfoSkipped', {
        reason: !isAuthenticated ? 'not_authenticated' : 'no_user_info'
      });
      return;
    }

    try {
      const userInfo = user;
      setAccessDenied(false);
      if (!userInfo) throw new Error("User info not available");

      const { displayName, preferredUserName, objectId } = userInfo;

      if (displayName) {
        setUserName(displayName);
      }

      // Track API call start
      const apiStartTime = Date.now();
      let userId = await getUserIdAndStore(preferredUserName);

      // Backend consent check temporarily disabled to fix build issue
      // TODO: Re-enable after fixing syntax error

      if (!userId) {
        const newUser = {
          teamsId: objectId,
          email: preferredUserName,
          name: displayName
        };

        try {
          const createStartTime = Date.now();
          
          // Track user creation attempt
          appInsightsService.trackEvent('CreateUserApiCall', {
            email: preferredUserName || 'none',
            name: displayName || 'none',
            teamsId: objectId || 'none'
          });
          
          const res = await postCreateUserApi(newUser);
          const createDuration = Date.now() - createStartTime;
          
          // Track creation response
          appInsightsService.trackEvent('CreateUserApiResponse', {
            duration: createDuration,
            isSuccess: res?.isSuccess || false,
            hasData: !!res?.data,
            userId: res?.data?.id || 'none',
            isNewUser: res?.data?.isNew || false,
            statusCode: res?.statusCode || 'unknown'
          });
          
          if (res?.isSuccess && res?.data?.id) {
            // User created or already exists - we get their data either way
            setUserToken(res.data.id);
            setAccess(true);
            userId = res.data.id;
            
            // Send timezone
            let timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            await sendTimeZone(res.data.id, timeZone);
            
            // Backend consent check temporarily disabled to fix build issue
            // TODO: Re-enable after fixing syntax error
          }
        } catch (error) {
          const createDuration = Date.now() - createStartTime;
          console.error('User creation error:', error);
          
          // Track creation error
          appInsightsService.trackEvent('CreateUserApiError', {
            duration: createDuration,
            errorMessage: error.message || 'Unknown error',
            errorType: error.name || 'UnknownError',
            statusCode: error.response?.status || 'unknown',
            responseData: JSON.stringify(error.response?.data || {}),
            fullError: JSON.stringify(error, Object.getOwnPropertyNames(error))
          });
        }
      }
    } catch (error) {
      console.error('User info fetch error:', error);
      
      // Track overall fetch error
      appInsightsService.trackEvent('FetchUserInfoError', {
        errorMessage: error.message || 'Unknown error',
        errorType: error.name || 'UnknownError',
        errorStack: error.stack || 'No stack trace',
        fullError: JSON.stringify(error, Object.getOwnPropertyNames(error))
      });
    }
  };

  const handleConsentCompleteWrapper = () => {
    setIsConsentComplete(true);
    // Reload to retry auth after consent
    window.location.reload();
  };

  const handleApiConsentClick = async () => {
    console.log('💆 User clicked API consent button - starting consent flow');
    try {
      if (teamsUserCredential && missingScopes.length > 0) {
        console.log('🔵 Requesting consent for scopes:', missingScopes);
        await teamsUserCredential.login(missingScopes);
        await teamsUserCredential.getToken(missingScopes);
        console.log('✅ API consent completed successfully');
        setApiConsentNeeded(false);
        setMissingScopes([]);
        // Trigger page reload to retry failed requests
        setTimeout(() => window.location.reload(), 1000);
      }
    } catch (error) {
      console.error('❌ API consent failed:', error);
    }
  };

  useEffect(() => {
    // Initialize Teams SDK v2 with detailed debugging
    console.log('🔍 Checking Teams SDK availability...');
    console.log('window.microsoftTeams exists:', !!window.microsoftTeams);
    console.log('window.microsoftTeams type:', typeof window.microsoftTeams);
    
    // Safe property extraction for mobile compatibility
    let sdkProperties = 'none';
    let sdkType = typeof window.microsoftTeams;
    
    if (window.microsoftTeams && typeof window.microsoftTeams === 'object') {
      try {
        sdkProperties = Object.keys(window.microsoftTeams).join(',');
      } catch (e) {
        console.error('Error getting Teams SDK properties:', e);
        sdkProperties = 'error-getting-keys';
      }
    } else if (window.microsoftTeams) {
      // SDK exists but is not an object (mobile case)
      sdkProperties = `non-object-type-${sdkType}`;
    }
    
    // Track Teams SDK availability with mobile-safe approach
    appInsightsService.trackEvent('TeamsSdkCheck', {
      exists: !!window.microsoftTeams,
      hasApp: !!(window.microsoftTeams && typeof window.microsoftTeams === 'object' && window.microsoftTeams.app),
      properties: sdkProperties,
      sdkType: sdkType,
      version: window.microsoftTeams?.version || 'unknown'
    });
    
    if (window.microsoftTeams && typeof window.microsoftTeams === 'object') {
      try {
        console.log('Teams SDK properties:', Object.keys(window.microsoftTeams));
        console.log('Teams SDK version info:', window.microsoftTeams.version || 'No version info');
      } catch (e) {
        console.log('Teams SDK exists but cannot enumerate properties:', e);
      }
      
      // Check if app object exists
      if (window.microsoftTeams.app && typeof window.microsoftTeams.app === 'object') {
        try {
          console.log('app object available:', Object.keys(window.microsoftTeams.app));
        } catch (e) {
          console.log('app object exists but cannot enumerate properties:', e);
        }
        
        // Initialize with safety check
        const initStartTime = Date.now();
        
        if (typeof window.microsoftTeams.app.initialize === 'function') {
          window.microsoftTeams.app.initialize()
            .then(() => {
            const initDuration = Date.now() - initStartTime;
            console.log('✅ Teams SDK initialized successfully');
            
            // Safe property extraction after init
            let postInitProperties = 'unknown';
            try {
              postInitProperties = Object.keys(window.microsoftTeams).join(',');
            } catch (e) {
              postInitProperties = 'error-getting-post-init-keys';
            }
            
            // Track successful initialization
            appInsightsService.trackEvent('TeamsSdkInitSuccess', {
              duration: initDuration,
              postInitProperties: postInitProperties,
              hasPages: !!window.microsoftTeams.pages
            });
            
            // Check what's available after initialization
            try {
              console.log('Post-init Teams SDK properties:', Object.keys(window.microsoftTeams));
            } catch (e) {
              console.log('Post-init Teams SDK exists but cannot enumerate properties');
            }
            
            if (window.microsoftTeams.pages && typeof window.microsoftTeams.pages === 'object') {
              try {
                console.log('pages object available:', Object.keys(window.microsoftTeams.pages));
              } catch (e) {
                console.log('pages object exists but cannot enumerate properties');
              }
            } else {
              console.log('❌ pages object still not available after init');
            }
            
            // DON'T call notifySuccess here - wait until content is ready
            // window.microsoftTeams.app.notifySuccess();
          })
          .catch((err) => {
            const initDuration = Date.now() - initStartTime;
            console.error('❌ Teams SDK initialization failed:', err);
            
            // Track initialization failure
            appInsightsService.trackEvent('TeamsSdkInitFailed', {
              duration: initDuration,
              errorMessage: err.message || 'No error message',
              errorType: err.name || 'UnknownError',
              errorStack: err.stack || 'No stack trace',
              fullError: JSON.stringify(err, Object.getOwnPropertyNames(err))
            });
            
            window.microsoftTeams.app.notifyFailure({
              reason: window.microsoftTeams.app.FailedReason.Other,
              message: "Failed to load app",
            });
          });
        } else {
          console.error('❌ Teams SDK app.initialize is not a function');
          appInsightsService.trackEvent('TeamsSdkInitNotFunction', {
            appType: typeof window.microsoftTeams.app,
            initializeType: typeof window.microsoftTeams.app.initialize
          });
        }
      } else {
        console.error('❌ Teams SDK app object not available');
        appInsightsService.trackEvent('TeamsSdkAppNotAvailable', {
          sdkType: typeof window.microsoftTeams
        });
      }
    } else {
      console.error('❌ Teams SDK not available on window object');
      appInsightsService.trackEvent('TeamsSdkNotAvailable', {
        windowKeys: Object.keys(window).filter(k => k.toLowerCase().includes('team')).join(',') || 'none'
      });
    }
  }, []);

  // 🔧 FIXED: Wait for credentials to be ready before making API calls
  useEffect(() => {
    if (isAuthenticated && credentialsReady) {
      console.log('🚀 CREDENTIALS READY: Starting fetchUserInfo');
      fetchUserInfo();
    } else {
      console.log('⏳ WAITING FOR CREDENTIALS:', { isAuthenticated, credentialsReady });
    }
  }, [isAuthenticated, credentialsReady]);

  useEffect(() => {
    fetchUserLocation();
  }, []);

  // Listen for reload message from auth popup
  useEffect(() => {
    const handleMessage = (event) => {
      console.log('Received message:', event.data);
      if (event.data && event.data.type === 'reload-after-consent') {
        console.log('Consent completed - clearing cache and reloading app...');
        // Clear any cached auth state that might be stale
        sessionStorage.clear();
        localStorage.removeItem('teamsUserCredential');
        // Hard reload to ensure fresh state
        window.location.href = window.location.href;
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // 🔐 SHADOW MODE LICENSE CHECK - Non-blocking, logging only
  // This hook is at the TOP LEVEL with all other hooks, before any conditional returns
  useEffect(() => {
    // Safety checks before running
    if (!isAuthenticated || !teamsUserCredential) {
      return;
    }
    
    const checkLicenseInShadowMode = async () => {
      try {
        const checkStartTime = Date.now();
        console.log('🔐 SHADOW MODE: Starting license check...');
        
        // Use the http.js get() function which handles Teams auth properly
        const http = (await import('../apis/http.js')).default;
        
        // Call the shadow mode license endpoint using http.js
        // http.js returns data directly, not wrapped in a response object
        const licenseData = await http.get('/license/status');
        
        if (licenseData) {
          const checkDuration = Date.now() - checkStartTime;
          
          const isProductionMode = !licenseData?.shadowMode;
          const modePrefix = isProductionMode ? 'PRODUCTION MODE' : 'SHADOW MODE';
          
          console.log(`📊 ${modePrefix}: License check response:`, licenseData);
          console.log(`   Duration: ${checkDuration}ms`);
          
          // Log Graph API usage rights details
          if (licenseData?.allServiceIdentifiers) {
            console.log(`🔐 ${modePrefix}: Graph API Usage Rights Details:`);
            console.log(`   Service Identifiers Found: ${licenseData.allServiceIdentifiers.join(', ') || 'None'}`);
            console.log(`   Has IzzyGo License: ${licenseData.actualLicenseStatus}`);
            console.log(`   License Details: ${licenseData.licenseDetails || 'N/A'}`);
          }
          
          // Check if user is from exempt tenant
          if (licenseData?.reason === 'exempt_tenant') {
            console.log(`🏢 ${modePrefix}: Your tenant is EXEMPT from licensing`);
            console.log('✅ License assumed for exempt tenant');
            console.log(`   Tenant ID: ${licenseData.tenantId}`);
            console.log(`   User ID: ${licenseData.userId}`);
            setIsExemptTenant(true);
            setTrialInfo(null); // Exempt tenants don't have trials
          } 
          // Check trial status for non-exempt tenants
          else if (licenseData?.trialInfo) {
            setTrialInfo(licenseData.trialInfo);
            setIsExemptTenant(false);
            
            if (licenseData.trialInfo.isInTrial) {
              console.log(`🕐 ${modePrefix}: Trial active - ${licenseData.trialInfo.daysRemaining} days remaining`);
            } else {
              console.log(`⏰ ${modePrefix}: Trial EXPIRED`);
            }
          }
          
          // Handle production mode blocking
          if (isProductionMode && licenseData?.shouldBlockUser) {
            console.log(`🚫 ${modePrefix}: User IS BLOCKED - no license and trial expired`);
            setHasAccess(false); // Actually block the user
          } else if (isProductionMode && licenseData?.licensed) {
            console.log(`✅ ${modePrefix}: User has access (license or trial)`);
            setHasAccess(true);
          } else if (!isProductionMode && licenseData?.wouldBlockInProduction) {
            console.log(`🎭 SHADOW MODE: User WOULD BE BLOCKED in production`);
          }
        } else {
          console.log('⚠️ SHADOW MODE: License endpoint returned no data');
        }
        
      } catch (error) {
        console.error('❌ SHADOW MODE: License check error (non-blocking):', error);
      }
    };
    
    // Run the shadow mode check after a short delay to ensure auth is ready
    const timer = setTimeout(checkLicenseInShadowMode, 3000);
    return () => clearTimeout(timer);
  }, [isAuthenticated, teamsUserCredential]);

  // 🔧 FIXED: Set Teams credential FIRST, then mark as ready
  useEffect(() => {
    console.log('🔍 CREDENTIAL DEBUG:', {
      teamsUserCredential: teamsUserCredential ? 'EXISTS' : 'NULL',
      isAuthenticated,
      isMobileDevice
    });
    
    // For mobile, create and set a Teams-compatible credential object
    if (isMobileDevice && isAuthenticated) {
      console.log('📱 Mobile: Creating and setting Teams credential for API calls');
      const mobileCredential = mobileAuthHandler.createCredential();
      setTeamsCredential(mobileCredential);
      setCredentialsReady(true);
    } else if (teamsUserCredential && isAuthenticated) {
      console.log('✅ Setting Teams credential in http.js');
      setTeamsCredential(teamsUserCredential);
      // 🎯 CRITICAL: Mark credentials as ready AFTER setting
      setCredentialsReady(true);
    } else {
      console.log('❌ NOT setting Teams credential - missing requirements');
      // SECURITY: Clear credential to prevent unauthorized API calls
      clearTeamsCredential();
      setCredentialsReady(false);
    }
  }, [teamsUserCredential, isAuthenticated, isMobileDevice]);

  // Azure AD handles consent in the popup - no separate UI needed

  // Handle API scope consent - but only if we're authenticated first
  if (apiConsentNeeded && missingScopes.length > 0 && isAuthenticated) {
    console.log('🔴 API consent needed for scopes:', missingScopes);
    // Try to get token silently first
    if (teamsUserCredential && !isLoading) {
      teamsUserCredential.getToken(missingScopes).then((token) => {
        console.log('✅ Got token silently, no consent needed!');
        setApiConsentNeeded(false);
        setMissingScopes([]);
        // Reload to retry API calls
        window.location.reload();
      }).catch((error) => {
        console.log('❌ Silent token failed, showing consent UI:', error);
        // Only show consent UI if silent token acquisition fails
        return (
          <TeamsFxContext.Provider value={{ theme, themeString, teamsUserCredential }}>
            <FluentProvider theme={theme || teamsLightTheme}>
              <div style={{ 
                padding: '40px', 
                maxWidth: '600px', 
                margin: '0 auto', 
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                borderRadius: '12px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
              }}>
                <h2 style={{ color: '#323130', marginBottom: '16px' }}>Additional Permissions Required</h2>
                <p style={{ color: '#605e5c', fontSize: '16px', lineHeight: '1.5', marginBottom: '24px' }}>
                  This app needs additional permissions to access your data.
                </p>
                <button 
                  onClick={() => {
                    if (teamsUserCredential) {
                      teamsUserCredential.login(missingScopes).then(() => {
                        setApiConsentNeeded(false);
                        setMissingScopes([]);
                        // Clear cache and reload
                        sessionStorage.clear();
                        window.location.reload();
                      }).catch(console.error);
                    }
                  }}
                  style={{
                    backgroundColor: '#0078d4',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '6px',
                    fontSize: '16px',
                    cursor: 'pointer',
                    marginRight: '12px'
                  }}
                >
                  Grant Additional Permissions
                </button>
              </div>
            </FluentProvider>
          </TeamsFxContext.Provider>
        );
      });
    }
    // Show loading while checking
    return (
      <TeamsFxContext.Provider value={{ theme, themeString, teamsUserCredential }}>
        <FluentProvider theme={theme || teamsLightTheme}>
          <Loader />
        </FluentProvider>
      </TeamsFxContext.Provider>
    );
  }

  // Notify Teams when app is ready to display (not loading)
  useEffect(() => {
    if (!isLoading && !isSigningIn && window.microsoftTeams?.app?.notifySuccess) {
      console.log('📢 Notifying Teams: App ready to display');
      window.microsoftTeams.app.notifySuccess();
    }
  }, [isLoading, isSigningIn]);
  
  // Initialize enhanced mobile tracking when authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      mobileEnhancedTracking.initialize();
    }
  }, [isAuthenticated, isLoading]);
  
  // Mobile Silent SSO Attempt
  useEffect(() => {
    console.log('🔍 SSO Effect Running:', {
      isAuthenticated,
      isSigningIn,
      hasCredential: !!teamsUserCredential,
      isMobile: navigator.userAgent.includes('TeamsMobile'),
      userAgent: navigator.userAgent
    });
    
    const attemptMobileSilentSSO = async () => {
      const isMobile = navigator.userAgent.includes('TeamsMobile');
      
      // CRITICAL FIX: Check for stored mobile token FIRST
      if (isMobile && !isAuthenticated && !isSigningIn) {
        const storedToken = sessionStorage.getItem('teams_auth_token');
        
        if (storedToken) {
          console.log('📱 Found stored mobile token - using it to authenticate...');
          setIsSigningIn(true);
          
          try {
            appInsightsService.trackEvent('MobileStoredTokenFound', {
              hasToken: true,
              tokenLength: storedToken.length
            });
            
            // Use the token to fetch user profile from Microsoft Graph
            const graphResponse = await fetch('https://graph.microsoft.com/v1.0/me', {
              headers: {
                'Authorization': `Bearer ${storedToken}`
              }
            });
            
            if (graphResponse.ok) {
              const userData = await graphResponse.json();
              console.log('✅ Mobile user profile fetched:', userData);
              
              appInsightsService.trackEvent('MobileGraphSuccess', {
                userId: userData.id,
                name: userData.displayName,
                email: userData.mail || userData.userPrincipalName
              });
              
              // Store authentication state for useEnhancedAuth to pick up
              // Use localStorage for mobile as sessionStorage might not persist across reload in Teams mobile
              const mobileUser = {
                displayName: userData.displayName,
                preferredUserName: userData.mail || userData.userPrincipalName,
                objectId: userData.id
              };
              
              localStorage.setItem('mobile_auth_user', JSON.stringify(mobileUser));
              localStorage.setItem('mobile_auth_complete', 'true');
              localStorage.setItem('mobile_auth_timestamp', Date.now().toString());
              setIsSigningIn(false);
              
              // Clear the token from storage after successful use
              sessionStorage.removeItem('teams_auth_token');
              
              // Reload to let useEnhancedAuth pick up the stored auth state
              console.log('🔄 Reloading to complete mobile authentication...');
              window.location.reload();
              
              return; // Exit - we're authenticated!
            } else {
              console.error('Graph API failed:', graphResponse.status);
              // Token might be expired, clear it
              sessionStorage.removeItem('teams_auth_token');
              
              appInsightsService.trackEvent('MobileGraphFailed', {
                status: graphResponse.status,
                statusText: graphResponse.statusText
              });
            }
          } catch (error) {
            console.error('Mobile token usage failed:', error);
            sessionStorage.removeItem('teams_auth_token');
            
            appInsightsService.trackEvent('MobileTokenError', {
              error: error.message
            });
          }
          
          setIsSigningIn(false);
        }
      }
      
      // Original TeamsFx silent SSO code (for web/desktop clients only - mobile uses stored token above)
      if (!isAuthenticated && !isSigningIn && teamsUserCredential && !isMobile) {
        
        console.log('🖥️ Web/Desktop client detected - attempting silent SSO with TeamsFx...');
        
        try {
          appInsightsService.trackEvent('WebDesktopSilentSSOAttempt', {
            timestamp: new Date().toISOString(),
            platform: navigator.userAgent.includes('TeamsDesktop') ? 'desktop' : 'web'
          });
          
          // Try silent token acquisition
          const tokenResponse = await teamsUserCredential.getToken(['User.Read']);
          
          if (tokenResponse && tokenResponse.token) {
            console.log('✅ Web/Desktop silent SSO successful!');
            
            appInsightsService.trackEvent('WebDesktopSilentSSOSuccess', {
              hasToken: true,
              timestamp: new Date().toISOString(),
              platform: navigator.userAgent.includes('TeamsDesktop') ? 'desktop' : 'web'
            });
            
            // Reload to pick up auth state
            window.location.reload();
          }
        } catch (error) {
          console.log('Web/Desktop silent SSO failed:', error.message);
          
          appInsightsService.trackEvent('WebDesktopSilentSSOFailed', {
            errorMessage: error.message,
            errorCode: error.errorCode || error.code,
            timestamp: new Date().toISOString(),
            platform: navigator.userAgent.includes('TeamsDesktop') ? 'desktop' : 'web'
          });
          
          // Will show sign-in screen
        }
      }
    };
    
    // Delay to ensure Teams SDK is ready
    const timer = setTimeout(attemptMobileSilentSSO, 2000);
    return () => clearTimeout(timer);
  }, [isAuthenticated, isSigningIn, teamsUserCredential]);

  // Mobile authentication is now handled properly in the Sign In button
  // using getAuthToken() and authenticate() methods
  
  // Show minimal content while loading to avoid React error
  // Teams will still show its native loader on top of this
  if (isLoading || isSigningIn) {
    console.log('📍 Loading state - Teams native loader should be visible');
    
    // Track loading state
    appInsightsService.trackEvent('AppLoadingState', {
      isLoading,
      isSigningIn,
      teamsFxLoading,
      enhancedAuthLoading: enhancedAuth.isLoading,
      hasCredential: !!teamsUserCredential,
      isAuthenticated
    });
    
    return (
      <TeamsFxContext.Provider value={{ theme, themeString, teamsUserCredential }}>
        <FluentProvider theme={theme || teamsLightTheme}>
          <div style={{ 
            height: '100vh', 
            width: '100%', 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5'
          }}>
            <Spinner size="large" label="Loading..." />
          </div>
        </FluentProvider>
      </TeamsFxContext.Provider>
    );
  }

  // If not authenticated and not loading, show Grant Permissions
  // BUT skip if we have mobile auth stored (prevents flash during reload)
  if (!isAuthenticated && !isLoading && !hasMobileAuth) {
    console.log('📍 SHOWING SIGN IN - not authenticated and not loading');
    console.log('📍 teamsUserCredential exists?', !!teamsUserCredential);
    
    // Track sign-in screen display
    appInsightsService.trackEvent('SignInScreenDisplayed', {
      hasCredential: !!teamsUserCredential,
      isAuthenticated,
      isLoading,
      isSigningIn,
      credentialsReady,
      user: user?.displayName || 'none',
      isMobile: navigator.userAgent.includes('TeamsMobile')
    });
    
    return (
      <TeamsFxContext.Provider value={{ theme, themeString, teamsUserCredential }}>
        <FluentProvider theme={theme || teamsLightTheme}>
          <div style={{ 
            padding: '40px', 
            maxWidth: '600px', 
            margin: '50px auto', 
            textAlign: 'center',
            backgroundColor: '#f8f9fa',
            borderRadius: '12px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ color: '#323130', marginBottom: '16px' }}>Welcome to izzygo</h2>
            <p style={{ color: '#605e5c', fontSize: '16px', lineHeight: '1.5', marginBottom: '24px' }}>
              {navigator.userAgent.includes('TeamsMobile') 
                ? 'Please sign in with your Teams account'
                : 'Please sign in to continue'}
            </p>
            {navigator.userAgent.includes('TeamsMobile') && (
              <p style={{ color: '#a19f9d', fontSize: '14px', marginBottom: '16px' }}>
                Note: If sign-in fails, try opening the app in Teams desktop
              </p>
            )}
            <button 
              onClick={async () => {
                console.log('🔵 Sign In button clicked');
                
                // Track sign-in attempt
                appInsightsService.trackAuthEvent('SignInButtonClicked', {
                  hasCredential: !!teamsUserCredential,
                  authState: isAuthenticated ? 'authenticated' : 'not_authenticated'
                });
                
                setIsSigningIn(true); // Start signing in
                
                // Check if mobile FIRST, before checking teamsUserCredential
                const isMobile = navigator.userAgent.includes('TeamsMobile');
                const startTime = Date.now();
                
                if (isMobile) {
                  console.log('📱 Mobile: Using Teams SDK authentication directly...');
                  
                  // Track login attempt
                  appInsightsService.trackAuthEvent('LoginAttemptStarted', {
                    method: 'mobile_teams_sdk',
                    scopes: 'User.Read',
                    isMobile: true
                  });
                  
                  // MOBILE: Use Teams SDK directly, no TeamsFx
                  if (window.microsoftTeams && window.microsoftTeams.authentication) {
                    try {
                      console.log('🔐 Attempting SSO with getAuthToken (Microsoft recommended)...');
                      
                      // First try SSO with getAuthToken as per Microsoft guidance
                      const authTokenRequest = {
                        successCallback: async (token) => {
                          console.log('✅ SSO successful via getAuthToken!');
                          
                          // Log token details (without exposing the actual token)
                          const tokenParts = token.split('.');
                          let tokenInfo = { error: 'Could not parse token' };
                          try {
                            const payload = JSON.parse(atob(tokenParts[1]));
                            tokenInfo = {
                              aud: payload.aud,
                              iss: payload.iss,
                              scp: payload.scp,
                              roles: payload.roles,
                              exp: new Date(payload.exp * 1000).toISOString(),
                              upn: payload.upn || payload.preferred_username
                            };
                          } catch (e) {
                            console.error('Token parse error:', e);
                          }
                          
                          console.log('📱 Token info:', tokenInfo);
                          
                          appInsightsService.trackAuthEvent('MobileSSO_TokenReceived', {
                            method: 'getAuthToken',
                            duration: Date.now() - startTime,
                            tokenAudience: tokenInfo.aud,
                            tokenScopes: tokenInfo.scp,
                            tokenExpiry: tokenInfo.exp
                          });
                          
                          // Decode the token to get user information
                          let userInfo;
                          try {
                            const payload = JSON.parse(atob(tokenParts[1]));
                            userInfo = {
                              name: payload.name,
                              preferred_username: payload.preferred_username,
                              upn: payload.upn,
                              email: payload.email || payload.mail,
                              oid: payload.oid,
                              sub: payload.sub,
                              given_name: payload.given_name,
                              family_name: payload.family_name
                            };
                          } catch (e) {
                            console.error('Failed to decode user info from token:', e);
                            throw new Error('Could not decode user information from token');
                          }
                          
                          // MOBILE SIMPLIFIED: Just authenticate without backend calls
                          // Backend calls will happen later when the app loads data
                          console.log('📱 Mobile: Setting user as authenticated');
                          console.log('📱 User info:', userInfo);
                          
                          try {
                            // Extract user email from the decoded token
                            const userEmail = userInfo.preferred_username || userInfo.upn || userInfo.email;
                            const userName = userInfo.name || `${userInfo.given_name || ''} ${userInfo.family_name || ''}`.trim() || 'User';
                            const userId = userInfo.oid || userInfo.sub;
                            
                            if (!userEmail) {
                              throw new Error('Could not extract email from token');
                            }
                            
                            // For mobile, store auth state and reload
                            console.log('✅ Mobile authentication successful');
                            
                            // Store authentication state for persistence
                            const mobileUserData = {
                              displayName: userName,
                              preferredUserName: userEmail,
                              objectId: userId
                            };
                            
                            // Store token and user info for future API calls
                            localStorage.setItem('teams_sso_token', token);
                            localStorage.setItem('mobile_auth_user', JSON.stringify(mobileUserData));
                            localStorage.setItem('mobile_auth_complete', 'true');
                            localStorage.setItem('mobile_auth_timestamp', Date.now().toString());
                            
                            // Store in mobileAuthHandler for API calls
                            mobileAuthHandler.storeAuthToken(token);
                            
                            // Keep isSigningIn true to show loading during reload
                            // setIsSigningIn(false); // DON'T set to false
                            
                            // Reload to apply auth state cleanly
                            console.log('🔄 Reloading to complete mobile authentication...');
                            window.location.reload();
                            
                            // Don't call backend here - let fetchUserInfo handle it  
                            console.log('📱 Mobile auth complete, backend calls will happen via fetchUserInfo');
                            
                            // Track successful mobile authentication
                            appInsightsService.trackAuthEvent('MobileAuth_Complete', {
                              email: userEmail,
                              name: userName,
                              method: 'MobileSSO'
                            });
                          } catch (error) {
                            console.error('Backend call error:', error);
                            
                            // Track the specific error
                            appInsightsService.trackAuthEvent('MobileBackendError', {
                              error: error.name === 'AbortError' ? 'timeout' : error.message,
                              duration: Date.now() - startTime
                            });
                            
                            // Fallback: Use authenticate flow
                            console.log('⚠️ Backend unavailable, using popup authentication...');
                            window.microsoftTeams.authentication.authenticate({
                              url: `${window.location.origin}/auth-start.html?clientId=${config.clientId}&isMobile=true`,
                              width: 600,
                              height: 535,
                              successCallback: (result) => {
                                console.log('✅ Popup authentication successful!', result);
                                
                                // Parse and store the result from auth-end.html
                                try {
                                  let authData = result;
                                  if (typeof result === 'string') {
                                    authData = JSON.parse(result);
                                  }
                                  
                                  // Store tokens in main window's sessionStorage
                                  if (authData.accessToken) {
                                    sessionStorage.setItem('access_token', authData.accessToken);
                                    // Store in mobileAuthHandler for API calls
                                    if (isMobileDevice) {
                                      mobileAuthHandler.storeAuthToken(authData.accessToken);
                                    }
                                  }
                                  if (authData.idToken) {
                                    sessionStorage.setItem('id_token', authData.idToken);
                                    
                                    // Decode the ID token to get user info
                                    try {
                                      const idTokenParts = authData.idToken.split('.');
                                      const payload = JSON.parse(atob(idTokenParts[1]));
                                      
                                      // Store user info from ID token
                                      const userInfo = {
                                        displayName: payload.name,
                                        preferredUserName: payload.preferred_username || payload.upn,
                                        objectId: payload.oid
                                      };
                                      sessionStorage.setItem('mobile_auth_user', JSON.stringify(userInfo));
                                      sessionStorage.setItem('mobile_auth_complete', 'true');
                                      
                                      console.log('✅ User info extracted from ID token:', payload.name);
                                    } catch (tokenError) {
                                      console.error('Failed to decode ID token:', tokenError);
                                    }
                                  }
                                  
                                  // Also store the full result for backward compatibility
                                  sessionStorage.setItem('auth_result', JSON.stringify(authData));
                                  sessionStorage.setItem('mobile_auth_complete', 'true');
                                  
                                  console.log('✅ Tokens and user info stored in sessionStorage');
                                } catch (e) {
                                  console.error('Failed to parse auth result:', e);
                                  // Fallback: store as-is
                                  if (result) {
                                    sessionStorage.setItem('auth_result', JSON.stringify(result));
                                  }
                                }
                                
                                setTimeout(() => {
                                  window.location.reload();
                                }, 500);
                              },
                              failureCallback: (authError) => {
                                console.error('❌ Authentication failed:', authError);
                                setIsSigningIn(false);
                                
                                // Better error message for mobile users
                                alert('Authentication failed. Please try again or contact support if the issue persists.');
                              }
                            });
                          }
                        },
                        failureCallback: (error) => {
                          console.log('⚠️ SSO failed, trying authenticate flow...', error);
                          
                          // If SSO fails, use authenticate as fallback
                          window.microsoftTeams.authentication.authenticate({
                            url: `${window.location.origin}/auth-start.html?clientId=${config.clientId}&isMobile=true`,
                            width: 600,
                            height: 535,
                            successCallback: (result) => {
                              console.log('✅ Mobile authentication successful!', result);
                              
                              // Parse and store the result from auth-end.html
                              try {
                                let authData = result;
                                if (typeof result === 'string') {
                                  authData = JSON.parse(result);
                                }
                                
                                // Store tokens in main window's sessionStorage
                                if (authData.accessToken) {
                                  sessionStorage.setItem('access_token', authData.accessToken);
                                  // Store in mobileAuthHandler for API calls
                                  if (isMobileDevice) {
                                    mobileAuthHandler.storeAuthToken(authData.accessToken);
                                  }
                                }
                                if (authData.idToken) {
                                  sessionStorage.setItem('id_token', authData.idToken);
                                  
                                  // Decode the ID token to get user info
                                  try {
                                    const idTokenParts = authData.idToken.split('.');
                                    const payload = JSON.parse(atob(idTokenParts[1]));
                                    
                                    // Store user info from ID token
                                    const userInfo = {
                                      displayName: payload.name,
                                      preferredUserName: payload.preferred_username || payload.upn,
                                      objectId: payload.oid
                                    };
                                    sessionStorage.setItem('mobile_auth_user', JSON.stringify(userInfo));
                                    sessionStorage.setItem('mobile_auth_complete', 'true');
                                    
                                    console.log('✅ User info extracted from ID token:', payload.name);
                                  } catch (tokenError) {
                                    console.error('Failed to decode ID token:', tokenError);
                                  }
                                }
                                
                                console.log('✅ Tokens stored in sessionStorage');
                              } catch (e) {
                                console.error('Failed to parse auth result:', e);
                              }
                              
                              appInsightsService.trackAuthEvent('MobileAuth_Success', {
                                method: 'authenticate_fallback',
                                duration: Date.now() - startTime
                              });
                              
                              setTimeout(() => {
                                window.location.reload();
                              }, 1000);
                            },
                            failureCallback: (authError) => {
                              console.error('❌ Both SSO and authenticate failed:', authError);
                              
                              appInsightsService.trackAuthEvent('MobileAuth_Failed', {
                                ssoError: error,
                                authError: authError,
                                duration: Date.now() - startTime
                              });
                              
                              setIsSigningIn(false);
                              alert('Authentication failed. Please try signing in from Teams desktop.');
                            }
                          });
                        }
                      };
                      
                      // Call getAuthToken for SSO
                      window.microsoftTeams.authentication.getAuthToken(authTokenRequest);
                      return; // Exit here, callbacks will handle the rest
                      
                    } catch (error) {
                      console.error('SSO setup failed:', error);
                      setIsSigningIn(false);
                    }
                  } else {
                    console.error('Teams SDK not available on mobile!');
                    appInsightsService.trackAuthEvent('MobileAuth_NoSDK', {
                      userAgent: navigator.userAgent
                    });
                    setIsSigningIn(false);
                    alert('Teams SDK not available. Please update your Teams app.');
                  }
                  
                } else if (teamsUserCredential) {
                  // DESKTOP: Use TeamsFx
                  console.log('🖥️ Desktop: Using TeamsFx authentication...');
                  
                  try {
                    // Track login attempt
                    appInsightsService.trackAuthEvent('LoginAttemptStarted', {
                      method: 'teamsUserCredential.login',
                      scopes: 'User.Read',
                      isMobile: false
                    });
                    
                    // Desktop - use normal popup auth
                    await teamsUserCredential.login(['User.Read']);
                    
                    const duration = Date.now() - startTime;
                    console.log('✅ Desktop login successful!');
                    
                    // Track success
                    appInsightsService.trackAuthEvent('LoginSucceeded', {
                      method: 'teamsUserCredential.login',
                      duration
                    });
                    
                    // Force a reload to pick up the new auth state
                    window.location.reload();
                  } catch (error) {
                    const duration = Date.now() - startTime;
                    console.error('❌ Desktop login failed:', error);
                    
                    // Track COMPREHENSIVE error details for mobile debugging
                    appInsightsService.trackAuthEvent('LoginFailed', {
                      // Error details
                      errorCode: error.errorCode || error.code || 'unknown',
                      errorMessage: error.message || 'No error message',
                      errorType: error.name || 'UnknownError',
                      errorStack: error.stack || 'No stack trace',
                      
                      // Error analysis
                      isCancelled: error.message?.includes('CancelledByUser'),
                      isConsentError: error.message?.includes('ConsentFailed'),
                      isNetworkError: error.message?.includes('network') || error.message?.includes('fetch'),
                      
                      // Timing
                      duration,
                      timestamp: new Date().toISOString(),
                      
                      // Device info
                      userAgent: navigator.userAgent,
                      screenSize: `${window.screen.width}x${window.screen.height}`,
                      devicePixelRatio: window.devicePixelRatio,
                      orientation: window.screen.orientation?.type || 'unknown',
                      
                      // App state
                      hasCredential: !!teamsUserCredential,
                      isAuthenticated,
                      isLoading,
                      credentialsReady,
                      
                      // Environment
                      url: window.location.href,
                      referrer: document.referrer,
                      online: navigator.onLine,
                      language: navigator.language,
                      
                      // Full error object (stringified)
                      fullError: JSON.stringify(error, Object.getOwnPropertyNames(error))
                    });
                    
                    // Track as exception for error analysis
                    appInsightsService.trackException(error, {
                      context: 'SignInButtonClick',
                      method: 'teamsUserCredential.login'
                    });
                    
                    // Check if user cancelled
                    if (error.message && error.message.includes('CancelledByUser')) {
                      console.log('User cancelled sign-in, no action needed');
                      setIsSigningIn(false); // Stop signing in
                      
                      // Track cancellation separately
                      appInsightsService.trackAuthEvent('LoginCancelled', {
                        reason: 'UserAction'
                      });
                      
                      // Don't show error for user cancellation
                      // Notify Teams that app is ready again
                      if (window.microsoftTeams?.app?.notifySuccess) {
                        window.microsoftTeams.app.notifySuccess();
                      }
                    } else if (error.message && error.message.includes('ConsentFailed')) {
                      // Consent issue - try to trigger consent flow
                      console.log('🔄 Consent failed, opening consent window...');
                      // Keep isSigningIn true during popup auth
                      // Open auth-start.html in a popup for consent
                      window.microsoftTeams.authentication.authenticate({
                        url: `${window.location.origin}/auth-start.html?clientId=${config.clientId}`,
                        width: 600,
                        height: 535,
                        successCallback: (result) => {
                          console.log('✅ Consent completed!');
                          // Don't reload immediately - the auth state should update
                          // window.location.reload();
                          // Instead, give time for tokens to be stored
                          setTimeout(() => {
                            window.location.reload();
                          }, 1000);
                        },
                        failureCallback: (error) => {
                          console.error('❌ Consent failed:', error);
                          setIsSigningIn(false); // Stop signing in on failure
                          // Notify Teams that app is ready (even though auth failed)
                          if (window.microsoftTeams?.app?.notifySuccess) {
                            window.microsoftTeams.app.notifySuccess();
                          }
                        }
                      });
                    } else {
                      // Other error
                      console.error('Sign-in error:', error);
                      setIsSigningIn(false); // Stop signing in
                    }
                  }
                } else {
                  console.error('❌ Teams credential not available');
                  appInsightsService.trackAuthEvent('LoginFailedNoCredential', {
                    reason: 'teamsUserCredential not available'
                  });
                  alert('Authentication service not available. Please refresh the page.');
                  setIsSigningIn(false); // Stop signing in
                }
              }}
              style={{
                backgroundColor: '#0078d4',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '6px',
                fontSize: '16px',
                cursor: 'pointer'
              }}
            >
              Sign In
            </button>
          </div>
        </FluentProvider>
      </TeamsFxContext.Provider>
    );
  }
  
  console.log('📍 SHOWING MAIN APP - authenticated');
  
  // Track main app display
  appInsightsService.trackEvent('MainAppDisplayed', {
    isAuthenticated,
    userName: userName || 'none',
    userToken: userToken || 'none',
    hasAccess: !accessDenied,
    credentialsReady
  });

  // PRODUCTION MODE: Check if user should be blocked
  const shouldShowTrialExpired = !hasAccess && trialInfo && !trialInfo.isInTrial && !isExemptTenant;
  
  // Log access decision
  if (shouldShowTrialExpired) {
    console.log('🚫 PRODUCTION MODE: Showing TRIAL EXPIRED screen - blocking user');
  }
  
  // Debug logging for trial banner
  const shouldShowTrialBanner = hasAccess && trialInfo && trialInfo.isInTrial && !isExemptTenant;
  console.log('🎯 ACCESS STATUS:', {
    hasAccess,
    trialInfo,
    isExemptTenant,
    shouldShowTrialBanner,
    shouldShowTrialExpired
  });
  
  if (shouldShowTrialBanner) {
    console.log('✅ PRODUCTION MODE: Showing TRIAL BANNER at bottom - Days remaining:', trialInfo.daysRemaining);
  } else if (shouldShowTrialExpired) {
    console.log('🚫 PRODUCTION MODE: TRIAL EXPIRED BLOCKING SCREEN - Trial ended', Math.abs(trialInfo.daysRemaining || 0), 'days ago');
  } else if (isExemptTenant) {
    console.log('🎖️ PRODUCTION MODE: Exempt tenant - full access granted');
  } else if (hasAccess) {
    console.log('✅ PRODUCTION MODE: User has valid license or active trial');
  } else {
    console.log('⏳ Waiting for license/trial check to complete...');
  }

  return (
    <TeamsFxContext.Provider value={{ theme, themeString, teamsUserCredential }}>
      <FluentProvider theme={theme || teamsLightTheme}>
        <ConfigProvider locale={antdLocaleFor(i18nInstance.language)}>
        <Router>
          {/* PRODUCTION MODE: Show trial banner for non-exempt tenants in trial */}
          {shouldShowTrialBanner && (
            <TrialBanner 
              daysRemaining={trialInfo.daysRemaining}
              onPurchaseClick={() => {
                console.log('🛒 SHADOW MODE: Purchase clicked from trial banner');
                // Open IzzyGo AppSource listing
                window.open('https://appsource.microsoft.com/en-us/marketplace/checkout/appdate1586417772427.izzygo?tab=Overview', '_blank');
              }}
            />
          )}
          
          {/* PRODUCTION MODE: Block users with expired trials and no license */}
          {shouldShowTrialExpired && ( // ACTUALLY BLOCKING IN PRODUCTION
            <TrialExpired />
          )}
          
          <UserContext.Provider
            // key on language: switching language remounts all content so EVERYTHING follows —
            // pages refetch with the new ?lang (translated titles/descriptions) and the Owl
            // carousels (which clone DOM outside React) are rebuilt in the new language.
            key={i18nInstance.language}
            value={{
              setUserName,
              userName,
              userToken,
              loading,
              accessDenied
            }}
          >
            {/* Header MUST be inside UserContext.Provider: LanguageSwitcher reads userToken
                (rendering it outside crashed the app on 2026-07-11 — context was undefined). */}
            <Header />

            <Routes>
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/termsofuse" element={<TermsOfUse />} />

              <Route path="/tab" element={<ProtectedRoute element={<Home />} />} />
              <Route path="/home" element={<ProtectedRoute element={<Home />} />} />
              <Route path="/video-catalogue" element={<ProtectedRoute element={<VideosCatalogue />} />} />
              <Route path="/blog-library" element={<ProtectedRoute element={<BlogLibrary />} />} />
              <Route path="/notification" element={<ProtectedRoute element={<Notification />} />} />
              <Route path="/book-mark" element={<ProtectedRoute element={<BookMarked />} />} />

              <Route path="/blog-detail" element={<ProtectedRoute element={<BlogDetail />} />} />
              <Route path="/video-detail" element={<ProtectedRoute element={<VideoDetail />} />} />
              <Route path="/" element={<Navigate to="/home" replace />} />
              <Route path="/access-denied" element={<AccessDenied />} />
              <Route path="*" element={<Navigate to="/access-denied" />} />
            </Routes>
          </UserContext.Provider>
          <Footer />
        </Router>
        </ConfigProvider>
      </FluentProvider>
    </TeamsFxContext.Provider>
  );
}

// Wrap the App component with AI tracking for component metrics
// This tracks component engagement time automatically
const AITrackedApp = withAITracking(appInsightsService.getReactPlugin(), App, 'App');
export default AITrackedApp;
